import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateControlledRiskOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateControlledRiskOversightResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export type UpdateControlledRiskOversightResultInput = {
  Id: string;
  Impact?: number | null;
  Likelihood?: number | null;
  Rating?: number | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useUpdateControlledRiskOversightResult = createMutationHook({
  name: 'updateControlledRiskOversightResult',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.assessment.updateControlledRiskOversightResult.mutationOptions(
      { onSuccess }
    ),
  mapInput: (input: UpdateControlledRiskOversightResultInput) => input,
  invalidate: (trpc) => [
    // Apollo parity: evictField(cache, 'risk_controlled_oversight_result')
    trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
    trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
    trpc.frontend.assessment.oversightResultById.pathKey(),
    trpc.frontend.assessment.riskOversightResultById.pathKey(),
    trpc.frontend.assessment.oversightAssessmentRiskAssessmentResultsByRiskId.pathKey(),
    trpc.frontend.assessment.latestOversightAssessmentRiskAssessmentResultsByRiskId.pathKey(),
    // Apollo parity: evictField(cache, 'risk_controlled_oversight_result_aggregate')
    trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
    // Apollo parity: evictField(cache, 'oversight_assessment')
    trpc.frontend.assessment.oversightActivityRegister.queryKey(),
    trpc.frontend.assessment.oversightActivityCount.queryKey(),
  ],
  mapResponse: (): UpdateControlledRiskOversightResultMutation => ({
    update_risk_controlled_oversight_result: {
      __typename: 'risk_controlled_oversight_result_mutation_response',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateControlledRiskOversightResultDocument,
  graphqlVariables: (input: UpdateControlledRiskOversightResultInput) => ({
    Id: input.Id,
    Impact: input.Impact,
    Likelihood: input.Likelihood,
    Rating: input.Rating,
    Rationale: input.Rationale,
    TestDate: input.TestDate,
    CustomAttributeData: input.CustomAttributeData ?? null,
  }),
  evictFields: [
    'oversight_assessment',
    'risk_controlled_oversight_result',
    'risk_controlled_oversight_result_aggregate',
  ],
});
